import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class ClarityService {
  private readonly clarityId = 't9xpim210i';

  constructor() {
    this.initializeClarity();
  }

  private initializeClarity(): void {
    if (typeof window !== 'undefined' && window.clarity) {
      // Clarity is already loaded, no need to reinitialize
      return;
    }

    // Initialize Clarity if not already loaded
    if (typeof window !== 'undefined') {
      (function (c: any, l: Document, a: string, r: string, i: string) {
        c[a] =
          c[a] ||
          function (...args: any[]) {
            (c[a].q = c[a].q || []).push(args);
          };
        const t = l.createElement(r) as HTMLScriptElement;
        t.async = true;
        t.src = 'https://www.clarity.ms/tag/' + i;
        const y = l.getElementsByTagName(r)[0];
        if (y && y.parentNode) {
          y.parentNode.insertBefore(t, y);
        }
      })(window, document, 'clarity', 'script', this.clarityId);
    }
  }

  /**
   * Track a custom event
   * @param eventName - Name of the event to track
   * @param data - Optional data to send with the event
   */
  trackEvent(eventName: string, data?: any): void {
    if (typeof window !== 'undefined' && window.clarity) {
      window.clarity('event', eventName, data);
    }
  }

  /**
   * Set custom tags for the current session
   * @param tags - Object containing key-value pairs of tags
   */
  setCustomTags(tags: Record<string, string>): void {
    if (typeof window !== 'undefined' && window.clarity) {
      // Clarity's 'set' API only accepts a single key/value pair per call
      Object.entries(tags).forEach(([key, value]) => {
        window.clarity('set', key, value);
      });
    }
  }

  /**
   * Identify a user
   * @param userId - Unique identifier for the user
   * @param sessionId - Optional session identifier
   * @param pageId - Optional page identifier
   * @param friendlyName - Optional friendly name for the user
   */
  identify(
    userId: string,
    sessionId?: string,
    pageId?: string,
    friendlyName?: string
  ): void {
    if (typeof window !== 'undefined' && window.clarity) {
      window.clarity('identify', userId, sessionId, pageId, friendlyName);
    }
  }

  /**
   * Track page views
   * @param pageName - Name of the page being viewed
   */
  trackPageView(pageName: string): void {
    if (typeof window !== 'undefined' && window.clarity) {
      // Clarity has no 'page' API method (calling one throws inside clarity.js);
      // it tracks SPA page views automatically, so record the route as a tag
      window.clarity('set', 'page', pageName);
    }
  }

  /**
   * Check if Clarity is loaded and ready
   * @returns boolean indicating if Clarity is available
   */
  isLoaded(): boolean {
    return typeof window !== 'undefined' && !!window.clarity;
  }
}
